import { forwardRef, type ComponentPropsWithoutRef } from "react";

import { SignalBadge } from "@/features/agentAttention/components/SignalBadge";
import type { AttentionItem } from "@/features/agentAttention/types";
import { cn } from "@/lib/utils";

import { groupLabel, groupMark, isPageGroup, type TabGroup } from "../groups";
import { useSortableTab } from "../hooks/useSortableTab";
import { tileWidth } from "../layout";
import { pageIcon, TabMark } from "./TabMark";

type Props = Omit<ComponentPropsWithoutRef<"button">, "children"> & {
  group: TabGroup;
  signals: AttentionItem[];
  running: boolean;
};

/**
 * A closed group: the client's avatar, stacked on a second disc when more than
 * one of its tabs is open, with the group's most urgent signal on the corner.
 * A page is its icon. Fixed width, so opening or closing another group never
 * resizes it; the mark sits at a fixed inset from the left (not centred) so
 * it holds still while the tile animates to or from a group's width.
 * Forwards its ref so the hover card can trigger from it.
 */
export const GroupTile = forwardRef<HTMLButtonElement, Props>(
  (
    { group, signals, running, className, style, onPointerDown, ...props },
    forwardedRef
  ) => {
    const page = isPageGroup(group);
    const stacked = group.tabs.length > 1 && !page;
    const PageIcon = pageIcon(group.tabs[0]?.pageId);
    const label = groupLabel(group);
    const n = group.tabs.length;
    const sortable = useSortableTab<HTMLButtonElement>(group.key, {
      forwardedRef,
      onPointerDown
    });
    return (
      <button
        ref={sortable.ref}
        type="button"
        role="tab"
        aria-selected={false}
        aria-label={n > 1 ? `${label}, ${n} tabs` : label}
        data-flip-key={group.key}
        data-group-key={group.key}
        data-dragging={sortable.isDragging ? "" : undefined}
        style={{ width: tileWidth(group), ...style, ...sortable.style }}
        onPointerDown={sortable.onPointerDown}
        className={cn(
          "relative flex h-7 shrink-0 items-center rounded-md transition-colors duration-100 hover:bg-fill-1 focus-visible:bg-fill-1 focus-visible:outline-none data-[dragging]:z-10 data-[dragging]:cursor-grabbing data-[dragging]:bg-elevated data-[dragging]:shadow-popover data-[resizing]:overflow-hidden data-[state=open]:bg-fill-2",
          "pl-1.5",
          className
        )}
        {...props}
      >
        <span className="relative flex shrink-0 items-center">
          <span
            data-flip-mark
            className={cn(
              "relative z-10 flex",
              stacked && "rounded-full ring-1 ring-chrome"
            )}
          >
            {page ? (
              <PageIcon className="size-4 text-fg-3" />
            ) : (
              <TabMark tab={groupMark(group)} />
            )}
          </span>
          {stacked ? (
            <span
              aria-hidden
              className="-ml-2.5 size-4 shrink-0 rounded-full bg-fill-3"
            />
          ) : null}
          <SignalBadge
            items={signals}
            running={running}
            variant="corner"
            className="z-20"
          />
        </span>
      </button>
    );
  }
);
GroupTile.displayName = "GroupTile";
